// The Home chat run (#1550). A question typed in the Home box becomes a `chat` request the hub records and a
// node answers with a short read-only model run. The pure parts live here: the DTO, the limits and the parser
// of the model's JSON reply. No I/O.

import type { HomeBoxKind } from "./home-box.ts";

export const HOME_CHAT_STATUSES = ["pending", "running", "answered", "failed"] as const;
export type HomeChatStatus = (typeof HOME_CHAT_STATUSES)[number];

/** How many earlier turns a chat request carries, and how long it waits for a node before the deterministic answer is used. */
export const HOME_CHAT_HISTORY_TURNS = 6;
export const HOME_CHAT_FALLBACK_MS = 20_000;

export type HomeChatTurn = { role: "you" | "office"; text: string };
export type HomeChatLink = { label: string; href: string };

export type HomeChatDTO = {
  id: string;
  projectId: string;
  askedBy: string;
  text: string;
  history: HomeChatTurn[];
  status: HomeChatStatus;
  /** Set once answered (`unclear` when the model could not tell). Null while pending or running. */
  kind: HomeBoxKind | null;
  answer: string | null;
  links: HomeChatLink[];
  /** True when the answer is the deterministic office status because no node could answer or the run failed. */
  fallback: boolean;
  /** Set when the model called the text an idea and the hub filed it (the same result POST /ideas gives). */
  filed: unknown | null;
  runId: string | null;
  createdAt: number;
  updatedAt: number;
};

export type HomeChatReply = { kind: HomeBoxKind; answerMarkdown: string; links: HomeChatLink[] };

/** Cuts a client's history to the last turns, dropping anything that is not a bounded text turn. */
export function trimChatHistory(raw: unknown): HomeChatTurn[] {
  if (!Array.isArray(raw)) return [];
  const turns: HomeChatTurn[] = [];
  for (const t of raw) {
    if (!t || typeof t !== "object") continue;
    const { role, text } = t as { role?: unknown; text?: unknown };
    if ((role !== "you" && role !== "office") || typeof text !== "string" || text.trim() === "") continue;
    turns.push({ role, text: text.slice(0, 1500) });
  }
  return turns.slice(-HOME_CHAT_HISTORY_TURNS);
}

const SAFE_HREF = /^(\/(?![/\\])|https?:\/\/)/;

function isLink(l: unknown): l is HomeChatLink {
  const x = l as HomeChatLink | null;
  return !!x && typeof x.label === "string" && typeof x.href === "string" && SAFE_HREF.test(x.href);
}

/**
 * Reads the model's final text as `{kind, answer_markdown, links}`. The model may wrap the JSON in a code fence or
 * prose, so the first balanced object that has an `answer_markdown` wins. Null when there is none (the caller falls
 * back). Links keep only relative paths and http(s) URLs.
 */
export function parseChatReply(text: string | null | undefined): HomeChatReply | null {
  if (!text) return null;
  for (let start = text.indexOf("{"); start >= 0; start = text.indexOf("{", start + 1)) {
    let depth = 0;
    let inString = false;
    for (let i = start; i < text.length; i++) {
      const ch = text[i]!;
      if (inString) {
        if (ch === "\\") i++;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') inString = true;
      else if (ch === "{") depth++;
      else if (ch === "}" && --depth === 0) {
        try {
          const obj = JSON.parse(text.slice(start, i + 1)) as Record<string, unknown>;
          const answer = typeof obj.answer_markdown === "string" ? obj.answer_markdown.trim() : "";
          if (answer) {
            const kind: HomeBoxKind = obj.kind === "idea" || obj.kind === "unclear" ? obj.kind : "question";
            const links = Array.isArray(obj.links) ? obj.links.filter(isLink).slice(0, 12).map((l) => ({ label: l.label.slice(0, 120), href: l.href.slice(0, 500) })) : [];
            return { kind, answerMarkdown: answer.slice(0, 8000), links };
          }
        } catch {
          // not JSON: try the next brace
        }
        break;
      }
    }
  }
  return null;
}
